import {createElement as h} from "react";

import {Types} from "@Common/types";

import {getNestedValue, setNestedValue} from "../../utils/arr-path";
import {equalDeep}                      from "../../utils/objects";
import {cs}                             from "../chain-services";
import {csArr}                          from "../cs-arr";
import {Static2}                        from "../static-2";
import {UseEffect}                      from "../use-effect";
import {UseState}                       from "../use-state";

export enum ValidationStatus {
    PENDING,
    FAILED,
    PASSED,
}

export const CsForm:<Model>(props: Types.Common.React.CsForm.CsFormProps<Model>) => any = ({fields, initValue=undefined, next}) => cs(
    ["valueStatic", ({}, next) => Static2({next, getInitValue: () => initValue})],
    ["fields", ({valueStatic: {get: getValue}}, next) => csArr(fields.map((field) =>
        (next) => cs(
            ["valueState", (_, next) => h(UseState, {
                getInitValue: () => getNestedValue(initValue, field.path.split(".")),
                next,
            })],

            ["validationsState", ({}, next) => UseState({next, initValue: []})],
            ["lastValidationStatic", ({}, next) => Static2({next, getInitValue: () => ({data: initValue, done: true, value: field.defaultValue, validators: []})})],
            ({lastValidationStatic, validationsState: {onChange: setValidations}, valueState}, next) => h(
                UseEffect,
                {
                    next,
                    fn: async () => {
                        const data       = getValue();
                        const value      = valueState.value ?? field.defaultValue;
                        const validators = field.validators ?? [];

                        // re-run validators reactively
                        const lastValidation = lastValidationStatic.get();
                        if (lastValidation.done && !equalDeep(lastValidation, {data, value, validators, done: true})) {
                            lastValidationStatic.set({data, value, validators, done: false});

                            const validations = validators.map(() => ValidationStatus.PENDING);
                            setValidations(validations);

                            validators.forEach(async (validator, index) => {
                                const passed = await validator.check(value, {data});
                                validations.splice(index, 1, ValidationStatus[passed ? "PASSED" : "FAILED"]);
                                setValidations(validations);
                            });
                            lastValidationStatic.set({data, value, validators, done: true});
                        }
                    }
                }
            ),

            ({validationsState: {value: validations}, valueState}) => {
                const value = valueState.value !== undefined ? valueState.value : field.defaultValue;
                return next({
                    validations,
                    value,

                    errors: validations.map((status, index) => (
                        status === ValidationStatus.PASSED ?
                            null
                        : status === ValidationStatus.FAILED ?
                            {getMessage: field.validators[index].getMessage}
                        :
                            {getMessage: field.validators[index].getPendingMessage ?? defaultPendingMessageGetter}
                    )).filter(Boolean) ?? [],

                    path: field.path,
                    validators: field.validators,
                    onChange: (v:any) => {
                        if (value !== v) {
                            const normalizedValue = field.defaultValue === v ? undefined : v;
                            valueState.onChange(normalizedValue);
                        }
                    },
                });
            },
        ),
    ), next)],

    ({fields, valueStatic}, next) => next(valueStatic.set((() => {
        let ret = {};
        for (const field of fields) {
            if (field.value === undefined) {
                continue;
            }
            ret = setNestedValue(ret, field.path.split("."), field.value);
        }
        return ret;
    })())),

    ({fields, valueStatic: {get: getValue}}) => {
        return next({
            field: (path) => {
                const field = fields.find((f) => f.path === path);
                return {
                    ...field,
                    getError: () => field.errors[0] ?? null,
                };
            },
            clear: () => {
                for (const field of fields) {
                    field.onChange(undefined);
                }
            },
            validate: () => {
                return fields.every(field => field.validations.every(status => status === ValidationStatus.PASSED));
            },
            value: getValue(),
            fromValue: (value) => {
                for (const field of fields) {
                    const fieldValue = getNestedValue(value, field.path.split("."));
                    field.onChange(fieldValue);
                }
            },
        });
    },
);

const defaultPendingMessageGetter = () => "Validating...";

// type helpers
export const makeFieldCreator = <Model>() => (
    <Path extends keyof Model & string, Field extends Types.Common.React.CsForm.Field<Model, Path>>(field:{path:Path} & Field) => field
);

